import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CircleCheck, CircleX, Trash2 } from 'lucide-react';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Estado } from '../../componentes/estados';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { AtividadesDaOportunidade } from './AtividadesDaOportunidade';
import { ModalGanhar, ModalPerder } from './Encerrar';
import { useNomesDaNegociacao } from './email-anexos';
import { FormOportunidade } from './FormOportunidade';
import { FormTarefa, rascunhoInicial } from './FormTarefa';
import { edicaoDaNegociacao, podeCriarTarefaNaNegociacao } from './negociacao-edicao';
import { CartaoVerticais } from '../../componentes/verticais';
import { ItensOportunidade } from './ItensOportunidade';
import type { Etapa, Oportunidade } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { rotuloEtapaPipeline, rotuloMotivoPerda, rotuloValor } from '../../nucleo/rotulos';
import { anunciarOcultos } from '../../componentes/sensivel';

const COR_ESTADO: Record<Oportunidade['estado'], CorSelo> = { aberta: 'azul', ganha: 'verde', perdida: 'perigo' };

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-tinta-3">{rotulo}</dt>
      <TextoCortado como="dd" className="min-w-0 text-right">{children}</TextoCortado>
    </div>
  );
}

/** Drawer da oportunidade: dados, etapa, ganhar e perder, atividades e chatter `pipeline.oportunidade`. */
export function DrawerOportunidade({ id, aoFechar, aoMudar }: { id: string; aoFechar: () => void; aoMudar: () => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  // Fase 2 (W25): depois de excluir, a gaveta não pede mais a oportunidade (segue montada até fechar).
  const [excluida, setExcluida] = useState(false);
  const q = useQuery({ queryKey: ['pipeline', 'oportunidade', id], queryFn: () => get<Oportunidade>(`/pipeline/oportunidades/${id}`), enabled: !excluida });
  // rl0311-6 VIS6-M1: o histórico da gaveta troca também contatos e empresas adicionais no modo discreto.
  const nomesDaNegociacao = useNomesDaNegociacao(id, q.data?.parceiro_nome, !excluida);
  const [ganhando, setGanhando] = useState(false);
  const [perdendo, setPerdendo] = useState(false);
  const o = q.data;
  // U13: as etapas do funil da oportunidade (ela só anda dentro dele).
  const etapas = useQuery({ queryKey: ['pipeline', 'etapas', o?.funil_id], queryFn: () => get<{ itens: Etapa[] }>('/pipeline/etapas', { funil_id: o!.funil_id }), enabled: !!o?.funil_id });
  const aberta = o?.estado === 'aberta';
  const pode = s.pode('pipeline.editar');
  // Lista de 30/10 (B), item 7.1: o "pode editar?" dos dados vem do ponto único da negociação (a trava da pausa entra lá).
  const edicao = o ? edicaoDaNegociacao(o, pode) : null;
  const podeDados = !!edicao?.dados;
  // Item 8: a pausada trava itens e verticais também; só a ficha em página retoma (com o motivo).
  const podeItens = !!edicao?.atividades;
  // Val5 (29/10): ganhar e perder têm ação própria (`pipeline.ganhar_perder`).
  const podeEncerrar = s.pode('pipeline.ganhar_perder');

  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: ['pipeline'] });
    await qc.invalidateQueries({ queryKey: ['chatter', 'pipeline.oportunidade', id] });
    aoMudar();
  };
  // Fase 2 (W25, K21): a aberta ou perdida vai para a lixeira (30 dias), com as atividades; a ganha não sai.
  const podeExcluir = !!o && o.estado !== 'ganha' && s.pode('pipeline.excluir');
  const [excluindo, setExcluindo] = useState(false);
  const [criarTarefa, setCriarTarefa] = useState(false);
  const excluir = useMutation({
    mutationFn: async () => {
      await del(`/pipeline/oportunidades/${id}`);
      setExcluida(true);
    },
    onSuccess: async () => {
      setExcluindo(false);
      toast.sucesso(tn('exclusao.oportunidade.feito'));
      aoFechar();
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
      aoMudar();
    },
    onError: (e) => {
      setExcluindo(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const mover = useMutation({
    mutationFn: (etapaId: string) => post(`/pipeline/oportunidades/${id}/mover`, { etapa_id: etapaId }),
    onSuccess: recarregar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={o ? textoLivre(o.titulo, o.parceiro_nome) : tn('comum.carregando')}
      rodape={
        podeDados || podeExcluir ? (
          <>
            {podeExcluir && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(true)}>
                {tn('exclusao.oportunidade.botao')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={aoFechar}>
              {podeDados ? tn('comum.cancelar') : tn('comum.fechar')}
            </Botao>
            {podeDados && (
              <Botao variante="primario" type="submit" form="form-oportunidade">
                {tn('comum.salvar')}
              </Botao>
            )}
          </>
        ) : undefined
      }
    >
      <Estado consulta={q}>
        {(op) => (
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Selo cor={COR_ESTADO[op.estado]}>{t(`estado.${op.estado}`)}</Selo>
              <Selo>{rotuloEtapaPipeline(op.etapa_nome)}</Selo>
              <span className="numero text-sm font-semibold">{anunciarOcultos(dinheiro(op.valor_centavos, op.moeda))}</span>
              <span className="numero text-xs text-tinta-3">{op.probabilidade}%</span>
              <Link to={`/cadastros/cadastros/parceiro/${op.parceiro_id}`} className="ml-auto text-sm text-acento-forte hover:underline">
                <TextoCortado className="block">
                  {nomePessoa(op.parceiro_nome)}
                </TextoCortado>
              </Link>
            </div>

            {edicao?.trava === 'pausada' && (
              <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2" role="status" data-aviso-pausada="">
                {t('crm.pausa.aviso')}
              </p>
            )}

            {podeDados && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-tinta-3">{t('drawer.mover')}</span>
                  {(etapas.data?.itens ?? [])
                    .filter((e) => e.tipo === 'aberta')
                    .map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        disabled={e.id === op.etapa_id || mover.isPending}
                        onClick={() => mover.mutate(e.id)}
                        className={`rounded-full border px-3 py-1 text-xs font-medium ${e.id === op.etapa_id ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-borda text-tinta-2 hover:text-tinta-1'}`}
                      >
                        {rotuloEtapaPipeline(e.nome)}
                      </button>
                    ))}
                </div>
                {podeEncerrar && (
                  <div className="flex flex-wrap gap-2">
                    <Botao variante="secundario" icone={<CircleCheck className="size-4" />} onClick={() => setGanhando(true)}>
                      {t('acoes.ganhar')}
                    </Botao>
                    <Botao variante="perigo" icone={<CircleX className="size-4" />} onClick={() => setPerdendo(true)}>
                      {t('acoes.perder')}
                    </Botao>
                  </div>
                )}
              </div>
            )}

            {aberta ? (
              <FormOportunidade atual={op} somenteLeitura={!podeDados} aoSalvar={recarregar} />
            ) : (
              <div>
                <p className="mb-3 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2">{t('drawer.encerrada')}</p>
                <dl className="divide-y divide-borda">
                  <Linha rotulo={t('campos.valor')}>{anunciarOcultos(dinheiro(op.valor_centavos, op.moeda))}</Linha>
                  <Linha rotulo={t('campos.responsavel')}>{op.responsavel_nome ?? tn('comum.nenhum')}</Linha>
                  <Linha rotulo={t('campos.equipe')}>{op.equipe_nome ?? tn('comum.nenhum')}</Linha>
                  <Linha rotulo={t('campos.origem')}>{[op.origem ? rotuloValor('origem', op.origem) : '', op.canal ? rotuloValor('canal', op.canal) : ''].filter(Boolean).join(' / ')}</Linha>
                  <Linha rotulo={op.estado === 'ganha' ? t('campos.ganhaEm') : t('campos.perdidaEm')}>{dataHora(op.ganha_em ?? op.perdida_em)}</Linha>
                  {op.motivo_perda_nome && <Linha rotulo={t('campos.motivo')}>{rotuloMotivoPerda(op.motivo_perda_nome)}</Linha>}
                  {op.projeto_id && (
                    <Linha rotulo={t('campos.projeto')}>
                      <Link to="/cadastros/cadastros/projetos" className="text-acento-forte hover:underline">
                        {t('drawer.projetoCriado')}
                      </Link>
                    </Linha>
                  )}
                  <Linha rotulo={t('campos.criadaEm')}>{data(op.created_at)}</Linha>
                </dl>
              </div>
            )}

            {/* V3 verticais: em quais verticais a oportunidade é trabalhada (os itens escolhem entre elas). */}
            <CartaoVerticais entidade="pipeline.oportunidade" registroId={op.id} podeEditar={podeItens} />

            {/* Trilha 4: itens da proposta (o contrato do ganho nasce com eles). A chave recria o editor ao recarregar. */}
            <ItensOportunidade key={`${op.id}:${op.updated_at}`} op={op} podeEditar={podeItens} aoSalvar={recarregar} />

            <AtividadesDaOportunidade oportunidade={op} />

            <div className="border-t border-borda pt-4">
              <Chatter entidade="pipeline.oportunidade" moeda={op.moeda} registroId={op.id} nomes={nomesDaNegociacao} titulo={op.titulo} criarTarefa={podeCriarTarefaNaNegociacao(op, s.pode('pipeline.criar')) ? () => setCriarTarefa(true) : null} />
            </div>
            {/* Lista de 02/11, item 6: o mesmo formulário do "Criar tarefa" da negociação. */}
            {criarTarefa && <FormTarefa travarNegociacao inicial={rascunhoInicial(op.responsavel_id ?? s.usuario.id, { parceiroId: op.parceiro_id, parceiroNome: op.parceiro_nome, oportunidadeId: op.id, oportunidadeTitulo: op.titulo })} aoFechar={() => setCriarTarefa(false)} />}

            <Confirmar
              aberto={excluindo}
              aoFechar={() => setExcluindo(false)}
              aoConfirmar={() => excluir.mutate()}
              carregando={excluir.isPending}
              perigoso
              titulo={tn('exclusao.oportunidade.titulo')}
              texto={tn('exclusao.oportunidade.texto')}
            />
            {ganhando && (
              <ModalGanhar
                oportunidade={op}
                aoFechar={() => setGanhando(false)}
                aoConcluir={async () => {
                  setGanhando(false);
                  await recarregar();
                }}
              />
            )}
            {perdendo && (
              <ModalPerder
                oportunidade={op}
                aoFechar={() => setPerdendo(false)}
                aoConcluir={async () => {
                  setPerdendo(false);
                  await recarregar();
                }}
              />
            )}
          </div>
        )}
      </Estado>
    </Drawer>
  );
}
